import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LabelsDropdown } from "@/components/popups/labels-dropdown";
import { cn } from "@/lib/utils";
import { Filter, X } from "lucide-react";
import { useState } from "react";

interface FilterOption {
  value: string;
  label: string;
  bgcolor?: string;
}

interface FilterLabelsEspButtonProps {
  labels: FilterOption[];
  selectedLabels: string[];
  onLabelsChange: (labels: string[]) => void;
  espTypes: FilterOption[];
  selectedEspTypes: string[];
  onEspTypesChange: (types: string[]) => void;
  className?: string;
}

export function FilterLabelsEspButton({
  labels,
  selectedLabels,
  onLabelsChange,
  espTypes,
  selectedEspTypes,
  onEspTypesChange,
  className,
}: FilterLabelsEspButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  const clearAllFilters = () => {
    onLabelsChange([]);
    onEspTypesChange([]);
  };

  const activeFiltersCount = selectedLabels.length + selectedEspTypes.length;

  return (
    <div className={cn("relative", className)}>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button 
            variant="outline" 
            className="h-9 px-3 border-border hover:bg-muted/50"
            onClick={() => setIsOpen(!isOpen)}
          >
            <Filter className="h-4 w-4" />
            <span className="hidden xs:block ml-2">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="ml-1 bg-primary text-primary-foreground text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center font-medium">
                {activeFiltersCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-60 max-h-[500px] overflow-y-auto bg-background border-border"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <DropdownMenuLabel className="flex items-center justify-between px-2 py-1.5">
            <span>Filters</span>
            {activeFiltersCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-5 px-1.5 text-xs text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3 mr-1" />
                Clear All
              </Button>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {/* Labels Filter */}
          <div className="px-2 py-1">
            <LabelsDropdown
              onLabelsChange={onLabelsChange}
              multiSelect={true}
              placeholder="Filter by labels"
              labels={labels}
              value={selectedLabels}
              align="start"
              className="w-full"
            />
          </div>
          
          {/* ESP Types Filter */}
          <div className="px-2 py-1">
            <LabelsDropdown
              onLabelsChange={onEspTypesChange}
              multiSelect={true}
              placeholder="Filter by ESP type"
              searchPlaceholder="Search ESP types..."
              labels={espTypes}
              value={selectedEspTypes}
              align="start"
              className="w-full"
            />
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
